import React from "react";
import {
  Box,
  Flex,
  Image,
  Text,
  Badge,
  HStack,
  Button,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import {
  BsArrowRight,
  BsPeople,
  BsCalendarEvent,
  BsTrophy,
} from "react-icons/bs";
import { HistoryTerm } from "constants/history";
import { colors } from "theme";

interface TermCardProps {
  term: HistoryTerm;
  onSelect: (id: string) => void;
}

export const TermCard: React.FC<TermCardProps> = ({ term, onSelect }) => {
  const [isHovered, setIsHovered] = React.useState(false);

  // Extract number for watermark (e.g. "gen-7" -> "07")
  const genNumberMatch = term.id.match(/\d+/);
  const genNumber = genNumberMatch
    ? genNumberMatch[0].padStart(2, "0")
    : "01";

  const accentColor = term.colorScheme || "google.green";

  return (
    <Box
      as={motion.article}
      w="full"
      h={{ base: "380px", sm: "400px", md: "420px", lg: "400px" }}
      position="relative"
      overflow="hidden"
      cursor="pointer"
      bg="gray.950"
      border="1px solid"
      borderColor={colors.primaryText}
      onClick={() => onSelect(term.id)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="group"
      aria-label={`Xem chi tiết ${term.title}`}
      whileHover="hover"
      initial="initial"
    >
      {/* ══════════════════════════════════════════════════════════════════════
          1. FULL-WIDTH PANORAMIC BACKGROUND IMAGE (100% COVER)
          ══════════════════════════════════════════════════════════════════════ */}
      <Box
        position="absolute"
        top={0}
        left={0}
        w="full"
        h="full"
        zIndex={1}
      >
        <motion.div
          style={{ width: "100%", height: "100%" }}
          variants={{
            hover: {
              scale: 1.05,
              transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
            },
            initial: {
              scale: 1,
              transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
            },
          }}
        >
          <Image
            height="100%"
            width="100%"
            objectFit="cover"
            src={term.coverImage}
            alt={term.title}
            loading="lazy"
            decoding="async"
            filter="brightness(0.9) contrast(1.05)"
          />
        </motion.div>
      </Box>

      {/* Lớp gradient đen tự nhiên cho trạng thái tĩnh */}
      <Box
        position="absolute"
        inset={0}
        bgGradient="linear(to-t, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.2) 55%, rgba(0,0,0,0.5) 100%)"
        zIndex={2}
        pointerEvents="none"
      />

      {/* ══════════════════════════════════════════════════════════════════════
          2. TRẠNG THÁI TĨNH (IDLE OVERLAY): POSTER TOÀN CẢNH
          Khi Hover: Mờ dần và trượt nhẹ lên (Fade-out) để hoàn toàn KHÔNG overlap
          ══════════════════════════════════════════════════════════════════════ */}
      <Box
        position="absolute"
        inset={0}
        zIndex={3}
        p={{ base: 6, sm: 8, md: 10 }}
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        pointerEvents="none"
        opacity={isHovered ? 0 : 1}
        transform={isHovered ? "translateY(-14px)" : "translateY(0)"}
        transition="opacity 0.25s ease-out, transform 0.25s ease-out"
        visibility={isHovered ? "hidden" : "visible"}
      >
        {/* Top Header */}
        <Flex justify="space-between" align="center">
          <HStack spacing={3}>
            <Badge
              bg="rgba(0, 0, 0, 0.75)"
              backdropFilter="blur(8px)"
              color="white"
              px={3}
              py={1}
              borderRadius="none"
              fontSize="xs"
              fontWeight="800"
              letterSpacing="1px"
              border="1px solid rgba(255, 255, 255, 0.2)"
            >
              {term.academicYear}
            </Badge>

            <Badge
              bg={accentColor}
              color="white"
              px={3}
              py={1}
              borderRadius="none"
              fontSize="xs"
              fontWeight="800"
              letterSpacing="1px"
              textTransform="uppercase"
            >
              {term.subTitle}
            </Badge>
          </HStack>

          {/* Watermark Number */}
          <Text
            fontSize={{ base: "3.5rem", sm: "4.5rem", md: "5.5rem" }}
            fontWeight="900"
            lineHeight="1"
            color="whiteAlpha.400"
            userSelect="none"
            letterSpacing="-2px"
            fontFamily="heading"
          >
            {genNumber}
          </Text>
        </Flex>

        {/* Bottom Title & Slogan */}
        <Box>
          <Text
            as="h2"
            fontSize={{ base: "30px", sm: "38px", md: "46px" }}
            fontWeight="900"
            color="white"
            letterSpacing="-1px"
            lineHeight="1.1"
            textShadow="0 4px 16px rgba(0, 0, 0, 0.7)"
          >
            {term.title}
          </Text>
          <Text
            fontSize={{ base: "sm", sm: "md", md: "lg" }}
            color="whiteAlpha.900"
            fontStyle="italic"
            fontWeight="500"
            mt={2}
            textShadow="0 2px 8px rgba(0, 0, 0, 0.8)"
          >
            "{term.slogan}"
          </Text>
        </Box>
      </Box>

      {/* ══════════════════════════════════════════════════════════════════════
          3. MÀN TRƯỢT MÀU GOOGLE (HOVER SLIDING CURTAIN REVEAL)
          Cuộn lên mượt mà từ đáy 100%, nền opaque 100% hoàn toàn che phủ ảnh gốc
          ══════════════════════════════════════════════════════════════════════ */}
      <Box
        as={motion.div}
        variants={{
          hover: {
            height: "100%",
            transition: {
              duration: 0.45,
              ease: [0.16, 1, 0.3, 1],
            },
          },
          initial: {
            height: 0,
            transition: {
              duration: 0.35,
              ease: [0.16, 1, 0.3, 1],
            },
          },
        }}
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        bg={accentColor}
        opacity={1}
        zIndex={4}
        overflow="hidden"
        color="white"
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        p={{ base: 6, sm: 8, md: 9 }}
      >
        {/* Nội dung bên trong Màn Trượt có animation staggered nhẹ để mượt mà */}
        <Box
          as={motion.div}
          variants={{
            hover: {
              opacity: 1,
              y: 0,
              transition: { duration: 0.35, delay: 0.15, ease: "easeOut" },
            },
            initial: {
              opacity: 0,
              y: 16,
              transition: { duration: 0.2 },
            },
          }}
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          h="full"
        >
          {/* Header trong màn hover */}
          <Box>
            <Flex justify="space-between" align="center" mb={2}>
              <HStack spacing={2.5}>
                <Badge
                  bg="white"
                  color={colors.primaryText}
                  px={2.5}
                  py={0.5}
                  borderRadius="none"
                  fontSize="2xs"
                  fontWeight="900"
                  letterSpacing="1px"
                >
                  HỒ SƠ THẾ HỆ
                </Badge>
                <Text
                  fontSize="2xs"
                  fontWeight="700"
                  letterSpacing="1px"
                  textTransform="uppercase"
                  opacity={0.9}
                >
                  {term.academicYear} • GDGoC - DUT
                </Text>
              </HStack>

              <Text
                fontSize="2xl"
                fontWeight="900"
                lineHeight="1"
                opacity={0.8}
                letterSpacing="-1px"
              >
                {genNumber}
              </Text>
            </Flex>

            {/* Title trong màn hover */}
            <Text
              fontSize={{ base: "22px", sm: "28px", md: "34px" }}
              fontWeight="900"
              letterSpacing="-0.5px"
              lineHeight="1.15"
              mb={1.5}
            >
              {term.title}
            </Text>

            {/* Slogan */}
            <Text
              fontSize={{ base: "xs", sm: "sm" }}
              fontStyle="italic"
              fontWeight="600"
              borderLeft="3px solid white"
              pl={2.5}
              mb={2.5}
            >
              "{term.slogan}"
            </Text>

            {/* Đoạn mô tả giới thiệu chi tiết */}
            <Text
              fontSize={{ base: "xs", sm: "sm" }}
              opacity={0.95}
              lineHeight="tall"
              maxW="850px"
              noOfLines={{ base: 2, sm: 3 }}
            >
              {term.description}
            </Text>
          </Box>

          {/* Thống kê hoạt động & Các ban chuyên môn */}
          <Box my={2}>
            <Flex
              direction={{ base: "column", md: "row" }}
              justify="space-between"
              align={{ base: "flex-start", md: "flex-end" }}
              gap={4}
            >
              {/* Cột số liệu */}
              <HStack spacing={{ base: 6, sm: 8 }}>
                {term.stats && term.stats.membersCount > 0 && (
                  <Box>
                    <Flex align="center" gap={1.5} fontSize="xs" opacity={0.9}>
                      <BsPeople size={14} />
                      <Text fontWeight="600">Thành viên</Text>
                    </Flex>
                    <Text fontSize={{ base: "2xl", sm: "3xl" }} fontWeight="900" lineHeight="1" mt={0.5}>
                      {term.stats.membersCount}+
                    </Text>
                  </Box>
                )}

                {term.stats && term.stats.eventsCount > 0 && (
                  <Box>
                    <Flex align="center" gap={1.5} fontSize="xs" opacity={0.9}>
                      <BsCalendarEvent size={14} />
                      <Text fontWeight="600">Sự kiện</Text>
                    </Flex>
                    <Text fontSize={{ base: "2xl", sm: "3xl" }} fontWeight="900" lineHeight="1" mt={0.5}>
                      {term.stats.eventsCount}+
                    </Text>
                  </Box>
                )}

                {term.stats?.highlights && (
                  <Box maxW="280px" display={{ base: "none", sm: "block" }}>
                    <Flex align="center" gap={1.5} fontSize="xs" opacity={0.9}>
                      <BsTrophy size={14} />
                      <Text fontWeight="600">Điểm nhấn</Text>
                    </Flex>
                    <Text fontSize="xs" fontWeight="700" noOfLines={2} mt={0.5}>
                      {term.stats.highlights}
                    </Text>
                  </Box>
                )}
              </HStack>

              {/* Các ban chuyên môn - ĐÃ NÂNG CẤP KÍCH THƯỚC RÕ RÀNG */}
              <Box display={{ base: "none", md: "block" }} textAlign="right" maxW="460px">
                <Text
                  fontSize="2xs"
                  letterSpacing="1.2px"
                  fontWeight="800"
                  opacity={0.9}
                  textTransform="uppercase"
                  mb={1.5}
                >
                  Đội ngũ ban chuyên môn
                </Text>
                <Flex wrap="wrap" gap={2} justify="flex-end">
                  {term.members.map((m) => (
                    <Badge
                      key={m.id}
                      bg="rgba(255, 255, 255, 0.25)"
                      _hover={{ bg: "white", color: colors.primaryText }}
                      transition="all 0.2s ease"
                      color="white"
                      px={3}
                      py={1}
                      borderRadius="none"
                      fontSize="xs"
                      fontWeight="700"
                      letterSpacing="0.3px"
                    >
                      {m.name}
                    </Badge>
                  ))}
                </Flex>
              </Box>
            </Flex>
          </Box>

          {/* Bottom CTA Action Button */}
          <Flex
            pt={3}
            borderTop="1px solid rgba(255, 255, 255, 0.3)"
            justify="space-between"
            align="center"
          >
            <Text fontSize="xs" opacity={0.9} display={{ base: "none", sm: "block" }}>
              Nhấp chuột để xem danh sách nhân sự và phóng sự hình ảnh
            </Text>

            <Button
              bg="white"
              color={colors.primaryText}
              _hover={{ bg: "gray.100", transform: "translateX(4px)" }}
              size="md"
              borderRadius="none"
              fontWeight="900"
              fontSize="xs"
              letterSpacing="1px"
              textTransform="uppercase"
              rightIcon={<BsArrowRight />}
              h="36px"
              px={5}
            >
              Khám phá thế hệ này
            </Button>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};
